import { useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Container } from '@/components/ui/Container'
import { cn } from '@/lib/utils'
import { useRef } from 'react'

export function Footer() {
  const { t } = useTranslation('common')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsSubmitting(true)
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setIsSubmitting(false)
    setIsSubscribed(true)
    setEmail('')
  }

  const footerLinks = {
    company: {
      title: t('footer.company'),
      links: [
        { label: t('nav.values'), href: '/company#valori' },
        { label: t('nav.history'), href: '/company#storia' },
        { label: t('nav.headquarters'), href: '/company#sede' },
        { label: t('nav.sustainability'), href: '/sustainability' },
      ],
    },
    products: {
      title: t('footer.products'),
      links: [
        { label: t('products.lines.vivaLaFrutta.name'), href: '/products?viva-la-frutta' },
        { label: t('products.lines.cheFruttaSecca.name'), href: '/products?che-frutta-secca' },
        { label: t('products.lines.bio.name'), href: '/products?bio' },
        { label: t('products.lines.100Italia.name'), href: '/products?100-italia' },
      ],
    },
    legal: {
      title: t('footer.navigation'),
      links: [
        { label: t('footer.privacy'), href: '/privacy' },
        { label: t('footer.cookiePolicy'), href: '/cookie-policy' },
      ],
    },
  }

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
    },
  }

  return (
    <footer ref={ref} className="bg-secondary text-white">
      {/* Main Footer Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="py-16 lg:py-20"
      >
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
            {/* Company Info Column */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <LocalizedLink to="/" className="inline-block mb-6 group">
                <img
                  src="/media/images/logos/Logo.png"
                  alt="Noberasco"
                  className="h-12 w-auto transition-transform duration-normal group-hover:scale-105"
                />
              </LocalizedLink>
              <p className="text-white/70 mb-8 max-w-sm leading-relaxed">
                {t('footer.tagline')}
              </p>

              {/* Social Links */}
              <div className="flex gap-4">
                <motion.a
                  href="https://www.instagram.com/noberasco"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent/30 transition-colors duration-normal"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </motion.a>
                <motion.a
                  href="https://www.facebook.com/noberasco"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent/30 transition-colors duration-normal"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </motion.a>
                <motion.a
                  href="https://www.linkedin.com/company/noberasco"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent/30 transition-colors duration-normal"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </motion.a>
              </div>
            </motion.div>

            {/* Navigation Links */}
            <motion.div variants={itemVariants}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-white/60">
                {footerLinks.company.title}
              </h3>
              <ul className="space-y-3">
                {footerLinks.company.links.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-white/70 hover:text-white hover:translate-x-1 transition-all duration-normal inline-block"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-white/60">
                {footerLinks.products.title}
              </h3>
              <ul className="space-y-3">
                {footerLinks.products.links.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-white/70 hover:text-white hover:translate-x-1 transition-all duration-normal inline-block"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Column */}
            <motion.div variants={itemVariants}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-white/60">
                {t('footer.contacts')}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm leading-relaxed">
                    {t('contact.address')}<br />
                    {t('contact.city')}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-accent shrink-0" />
                  <a
                    href={`tel:${t('contact.phone').replace(/\s/g, '')}`}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {t('contact.phone')}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-accent shrink-0" />
                  <a
                    href={`mailto:${t('contact.email')}`}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {t('contact.email')}
                  </a>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Newsletter Section */}
          <motion.div
            variants={itemVariants}
            className="mt-16 pt-12 border-t border-white/10"
          >
            <div className="max-w-xl">
              <h3 className="font-heading font-semibold text-xl mb-2 text-white">
                {t('footer.newsletter.title')}
              </h3>
              <p className="text-white/60 mb-6 text-sm">
                {t('footer.newsletter.subtitle')}
              </p>

              {isSubscribed ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-accent/20 border border-accent/30 rounded-2xl px-6 py-4"
                >
                  <p className="text-accent font-medium">
                    {t('footer.newsletter.subscribed')}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex gap-3">
                  <label htmlFor="newsletter-email" className="sr-only">
                    {t('footer.newsletter.placeholder')}
                  </label>
                  <input
                    id="newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer.newsletter.placeholder')}
                    required
                    aria-label={t('footer.newsletter.placeholder')}
                    className="flex-1 px-5 py-3.5 bg-white/10 border border-white/20 rounded-full text-white placeholder:text-white/40 focus:outline-none focus:border-accent focus:bg-white/15 transition-all duration-normal"
                  />
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      'px-6 py-3.5 bg-accent text-secondary font-semibold rounded-full hover:bg-accent/90 transition-all duration-normal shadow-lg hover:shadow-xl disabled:opacity-50',
                      isSubmitting ? 'animate-pulse' : ''
                    )}
                  >
                    {isSubmitting ? '...' : t('buttons.send')}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </Container>
      </motion.div>

      {/* Bottom Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="border-t border-white/10 py-6"
      >
        <Container>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
            <p>
              &copy; {new Date().getFullYear()} Noberasco. {t('footer.allRightsReserved')}
            </p>
            <div className="flex gap-6">
              <LocalizedLink
                to="/privacy"
                className="hover:text-white transition-colors duration-normal"
              >
                {t('footer.privacy')}
              </LocalizedLink>
              <LocalizedLink
                to="/cookie-policy"
                className="hover:text-white transition-colors duration-normal"
              >
                {t('footer.cookiePolicy')}
              </LocalizedLink>
            </div>
          </div>
        </Container>
      </motion.div>
    </footer>
  )
}
